// src/styles/theme.ts
//
// The single source of truth for every colour and every scale in the app.
//
// WHY THIS FILE IS SHAPED THIS WAY
//
// A theme here is data, not behaviour. Components never read a raw hex value or
// branch on the current mode; they read a token (`theme.text`, `theme.surface`,
// `theme.shadows.md`) and the palette behind that token changes underneath them.
// That is what makes the light theme a whole-site change rather than a page-by-
// page edit, and it is why nothing in components/ or pages/ needs to know a
// light theme exists.
//
// The file is split in three parts:
//
//   1. INVARIANT SCALES. Spacing, type sizes, radii and breakpoints are the same
//      numbers in every theme -- only colour changes between modes. They are
//      declared once and spread into each theme, so a scale can never drift
//      between light and dark.
//   2. THE COLOUR CONTRACT. ThemeColors is the interface every palette must
//      satisfy. Adding a theme is therefore a compile error if a token is
//      missing, which is the point: a half-specified theme is a bug that should
//      not be deployable.
//   3. THE PALETTES AND THE REGISTRY. darkTheme is the default and its values
//      are the long-standing ones. lightTheme is the off-white / black
//      counterpart. Both are registered in `themes` and reached through
//      `getTheme`, so selecting a mode never requires a ternary in App.tsx.
//
// Adding a third theme is a palette object plus one line in `themes`. No other
// file changes.

/** The selectable modes. Kept here, next to the palettes it indexes. */
export type ThemeMode = 'light' | 'dark';

/* --------------------------------------------------------------- 1. scales */

/** Identical in every theme: these are geometry, not palette. */
const spacing = {
  sm: '0.5rem',
  md: '1rem',
  lg: '1.5rem',
  xl: '2rem',
  '2xl': '3rem',
  '3xl': '4rem',
};

const fontSizes = {
  sm: '0.875rem',
  md: '1rem',
  lg: '1.25rem',
  xl: '1.5rem',
};

const borderRadius = {
  sm: '4px',
  md: '8px',
  lg: '12px',
  full: '9999px',
};

/**
 * Also exported on its own from styles/breakpoints.ts, which is what
 * media-query helpers use. It is repeated on the theme so a styled component can
 * write a breakpoint inline without a second import.
 */
const breakpoints = {
  sm: '480px',
  md: '768px',
  lg: '1024px',
  xl: '1200px',
};

/* ------------------------------------------------------ 2. colour contract */

/**
 * Everything a palette has to supply. Shadows live here rather than with the
 * invariant scales because a light theme needs far less shadow than a dark one:
 * on off-white, a shadow strong enough to read against near-black reads as dirt.
 */
export interface ThemeColors {
  /** Page canvas. Sits behind every section. */
  background: string;
  /** Default body text. */
  text: string;
  /** Section and card surface, one step off the canvas in each direction. */
  surface: string;
  /** Headings. Often a shade stronger than `text`. */
  headingColor: string;
  /** Solid fill for primary actions. Paired with `background` as its label. */
  primary: string;
  /** Interactive accent: focus rings, active route markers, CTAs. */
  accent: string;

  /**
   * The required-field marker, and the only chromatic token in the palette. Every
   * other colour here is a grey step, which is the point of the monochrome
   * design, but a validation cue has to read as a different kind of thing rather
   * than merely a different weight. Declared as a token for the same reason as
   * every other colour in this file: a component that hard-codes a hex breaks the
   * moment a second theme exists.
   */
  required: string;

  /** Tinted fill for cards that sit on the canvas. */
  cardBg: string;
  /** Full border shorthand, for the many places that want a one-liner. */
  cardBorder: string;
  /** Bare border colour, for the places that need a side or bottom only. */
  borderColor: string;

  /** Page-level background image, including the gradient layers above it. */
  heroBackground: string;

  /** Tokens for the existing toggle control. */
  toggleBg: string;
  buttonBg: string;
  buttonText: string;
  buttonShadow: string;

  /** Grouped palette for the components that want a named slot. */
  colors: {
    background: string;
    text: string;
    primary: string;
    /** Supporting copy: card bodies, captions, de-emphasised labels. */
    secondary: string;
    accent: string;
  };

  shadows: {
    sm: string;
    md: string;
    lg: string;
  };
}

/** A complete theme: the colour contract plus the invariant scales. */
export interface Theme extends ThemeColors {
  spacing: typeof spacing;
  fontSizes: typeof fontSizes;
  borderRadius: typeof borderRadius;
  breakpoints: typeof breakpoints;
}

/* ------------------------------------------------------------ 3. palettes */

/**
 * Dark is the default and is deliberately unchanged. This palette is what the
 * site has always looked like, so it is reproduced value for value rather than
 * tidied up in passing.
 */
const darkColors: ThemeColors = {
  background: '#0b0b0b',
  text: '#f5f5f5',
  toggleBg: '#1f1f1f',
  buttonBg: '#1a1a1a',
  buttonText: '#f5f5f5',
  buttonShadow: '0 6px 16px rgba(0, 0, 0, 0.45)',
  heroBackground:
    'radial-gradient(1200px 600px at 0% 0%, rgba(255,255,255,0.03), transparent 60%), ' +
    'radial-gradient(900px 500px at 100% 100%, rgba(255,255,255,0.02), transparent 55%), ' +
    '#0b0b0b',
  headingColor: '#ffffff',
  primary: '#f5f5f5',
  accent: '#a9a9a9',
  /* Lifted for dark: a mid red that still reads as red would sit too close to
     the canvas. About 7:1 on #0b0b0b. */
  required: '#f87171',

  surface: '#121212',
  cardBg: 'rgba(255,255,255,0.04)',
  cardBorder: '1px solid rgba(255,255,255,0.08)',
  borderColor: 'rgba(255,255,255,0.08)',

  colors: {
    background: '#0b0b0b',
    text: '#f5f5f5',
    primary: '#f5f5f5',
    secondary: '#c2c2c2',
    accent: '#a9a9a9',
  },

  shadows: {
    sm: '0 1px 4px rgba(0, 0, 0, 0.4)',
    md: '0 6px 14px rgba(0, 0, 0, 0.45)',
    lg: '0 14px 28px rgba(0, 0, 0, 0.5)',
  },
};

/**
 * Light: black text on an off-white canvas.
 *
 * The inversion is not a colour swap. Three things are deliberately different
 * from a naive "light = flipped dark":
 *
 *   - `background` is off white (#faf9f7), not #fff, and `surface` is pure white
 *     against it. Sections and the footer then read as white cards on a warm
 *     canvas instead of dissolving into it.
 *   - `primary` and `accent` go dark. Both are used as a fill with
 *     `theme.background` as the label, so a light-theme accent has to be a dark
 *     grey to keep that pairing legible.
 *   - The glows in `heroBackground` are black at low alpha rather than white.
 *     White glows are invisible on off-white, which would flatten the hero.
 *
 * Body text is #000 rather than a near-black: 21:1 against the canvas, and the
 * user asked for black. `colors.secondary` is the token for supporting copy and
 * is held at #4b4b4b (about 8.6:1) so it stays readable at small sizes.
 */
const lightColors: ThemeColors = {
  background: '#faf9f7',
  text: '#000000',
  toggleBg: '#e6e4e0',
  buttonBg: '#111111',
  buttonText: '#faf9f7',
  buttonShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  heroBackground:
    'radial-gradient(1200px 600px at 0% 0%, rgba(0,0,0,0.035), transparent 60%), ' +
    'radial-gradient(900px 500px at 100% 100%, rgba(0,0,0,0.025), transparent 55%), ' +
    '#faf9f7',
  headingColor: '#000000',
  primary: '#111111',
  accent: '#3d3d3d',
  /* Darker than the dark-theme value: on off-white, a light red would be the
     least legible thing on the page. About 6:1 on #faf9f7. */
  required: '#b91c1c',

  surface: '#ffffff',
  cardBg: 'rgba(0, 0, 0, 0.03)',
  cardBorder: '1px solid rgba(0, 0, 0, 0.1)',
  borderColor: 'rgba(0, 0, 0, 0.1)',

  colors: {
    background: '#faf9f7',
    text: '#000000',
    primary: '#111111',
    secondary: '#4b4b4b',
    accent: '#3d3d3d',
  },

  shadows: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.06)',
    md: '0 4px 12px rgba(0, 0, 0, 0.08)',
    lg: '0 12px 28px rgba(0, 0, 0, 0.1)',
  },
};

const shared = { spacing, fontSizes, borderRadius, breakpoints };

/** The default theme, and the only one with no browser-side opt-in needed. */
export const darkTheme: Theme = { ...shared, ...darkColors };

/** Black on off-white. Selectable, but never the default. */
export const lightTheme: Theme = { ...shared, ...lightColors };

/* --------------------------------------------------------------- registry */

/**
 * The lookup table. App.tsx resolves a mode through this and never writes its
 * own conditional, so registering a theme here is all it takes to make it
 * reachable from the whole site.
 */
export const themes: Record<ThemeMode, Theme> = {
  light: lightTheme,
  dark: darkTheme,
};

/** Dark unless something says otherwise. */
export const DEFAULT_THEME_MODE: ThemeMode = 'dark';

/**
 * Resolves a mode to its theme. Total by construction: `ThemeMode` and the keys
 * of `themes` are checked against each other, so a mode with no palette is a
 * compile error rather than a blank page.
 */
export const getTheme = (mode: ThemeMode): Theme => themes[mode];

/** Narrows an untrusted value -- a localStorage string -- to a real mode. */
export const isThemeMode = (value: unknown): value is ThemeMode =>
  typeof value === 'string' && Object.prototype.hasOwnProperty.call(themes, value);

/**
 * Kept for styled.d.ts, which extends styled-components' DefaultTheme with it.
 * The name is historical; the shape is the full Theme.
 */
export type ThemeType = Theme;
